import type { PriceType } from "@core/types"
import type { CtaAXLink } from "@gatsby-plugin-generic-page/fragments/ctaAXLink"
import { getDomainsPoolId } from "@gatsby-plugin-generic-page/helpers/getDomainsPoolId"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import queryString from "query-string"
import { STORAGE } from "~/lib/storage"
import SessionStorage from "~/lib/storage/SessionStorage"
import isEmpty from "./isEmpty"
import vars from "./onestoreVars"

export const QUICK_PURCHASE_SUMMARY_PATH = "/zamowienie/kup-teraz/"

export function getQueryParam(
  name: string,
  url: string | null = null
): string | null {
  if (!url) {
    url = window.location.href
  }
  name = name.replace(/[\[\]]/g, "\\$&")
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`)
  const results = regex.exec(url)

  if (!results) {
    return null
  }

  if (!results[2]) {
    return ""
  }

  return decodeURIComponent(results[2].replace(/\+/g, " "))
}

export interface OnestoreVarsUrl {
  basketUrl: string
  bonusUrl: string
  domainsBonusUrl: string
  domainSearchUrl: string
  failedUrl: string
  finalizeUrl: string
  helpUrl: string
  passwordRecoverUrl: string
  priceListPath: string
  transferPagePath: string
  successfulUrl: string
  wikiUrl: string
  helpContactUrl: string
}

export interface BonusUrl {
  p?: string | PlanSettings
  quantity?: number
  ax?: (string | PlanSettings)[]
  b?: string
  u?: string
  code?: string | null
  period?: string
  domainSearch?: string | null
  d?: string
  checkedPeriod?: string
  checkoutFastMode?: string
  priceType: PriceType
  isNotCheckedPeriod?: boolean
}

export interface OnestoreUrl {
  generateBonusUrl: { (params: BonusUrl, externalHost?: string): string }
  getBasketUrl: { (): string }
  getFinalizeUrl: { (): string }
  getFailureUrl: { (): string }
  getSuccessfulUrl: { (): string }
  getDomainSearchUrl: { (): string | null }
  getPasswordRecoverUrl: { (): string }
  getPanelUrl: { (): string }
  getBonusUrl: { (): string }
  getDomainsBonusUrl: { (): string | null }
  generateBonusUrlByCtaAxLink: {
    (ctaAxLink: CtaAXLink, currentPriceType: PriceType): string
  }
}

export interface PlanSettings {
  alias: string
  quantity?: number
  parameters?: Record<string, string>
  resources?: Record<string, string>
  period?: string
}

export function getSettingsString(plan: string | PlanSettings): string {
  if (typeof plan === "string") {
    return plan
  }

  const planParams: string[] = []

  for (const paramName in plan.parameters) {
    planParams.push(`param[${paramName}]:${plan.parameters[paramName]}`)
  }

  for (const paramName in plan.resources) {
    planParams.push(`resource[${paramName}]:${plan.resources[paramName]}`)
  }

  if (plan.period) {
    planParams.push(`period:${plan.period}`)
  }

  if (plan.quantity) {
    planParams.push(`quantity:${plan.quantity}`)
  }

  return `${plan.alias}(${planParams.join(";")})`
}

export type BonusUrlParams = Omit<
  BonusUrl,
  "p" | "checkoutFastMode" | "isNotCheckedPeriod"
> & {
  p?: string
  checkoutMode?: string
}

const generateBonusUrl = (
  {
    p,
    ax,
    b,
    u,
    code,
    period,
    domainSearch,
    d,
    checkoutFastMode,
    priceType,
    isNotCheckedPeriod,
  }: BonusUrl,
  externalHost?: string
): string => {
  const params: BonusUrlParams = { priceType }

  if (p) {
    params.p = getSettingsString(p)
  }

  if (b) {
    params.b = b
  }

  if (u) {
    params.u = u
  }

  if (code) {
    params.code = code
  }

  if (period) {
    // If redirection is with a promo code or isNotCheckedPeriod is true, we displaying only one period
    if (code || isNotCheckedPeriod) {
      params.period = period
    } else {
      params.checkedPeriod = period
    }
  }

  if (checkoutFastMode) {
    params.checkoutMode = checkoutFastMode
  }

  if (domainSearch) {
    params.domainSearch = domainSearch
  }

  if (ax) {
    const upsell: string[] = []

    ax.forEach((value: string | PlanSettings) => {
      upsell.push(getSettingsString(value))
    })

    if (upsell.length) {
      params.ax = upsell
    }
  }

  if (d) {
    params.d = d
  }

  if (priceType) {
    params.priceType = priceType
  }

  const query = queryString.stringify(params, { arrayFormat: "index" })

  if (externalHost) {
    return `${externalHost.replace(/\/+$/, "")}/?${query}`
  }

  return `${vars.bonusUrl}?${query}`
}

const getBasketUrl = (): string => vars.basketUrl

const getDomainsBonusUrl = (): string | null => vars.domainsBonusUrl || null

const getFinalizeUrl = (): string => vars.finalizeUrl

const getFailureUrl = (): string => vars.failedUrl

const getSuccessfulUrl = (): string => vars.successfulUrl

const getDomainSearchUrl = (): string | null => vars.domainSearchUrl || null

const getPasswordRecoverUrl = (): string => vars.passwordRecoverUrl

const getPanelUrl = () =>
  SessionStorage.get(STORAGE.CONTROL_PANEL) || vars.panelUrl

const getBonusUrl = () => vars.bonusUrl || ""

export function getLinkPropForUrl(url?: string): {
  href?: string
  to?: string
} {
  if (url === undefined || url.includes("://")) {
    return {
      href: url,
    }
  }

  return {
    to: url,
  }
}

export function getPromptUrl(hash: string): string {
  const match = hash.match(/p:([^,]*)/)

  return !isEmpty(match) ? match[1] : ""
}

export function generateBonusUrlByCtaAxLink(
  ctaAxLink: CtaAXLink,
  currentPriceType: PriceType,
  selectedPeriod?: string
): string {
  const saleConfiguration = !isEmpty(ctaAxLink.saleConfiguration)
    ? ctaAxLink.saleConfiguration[0]
    : null

  // Get data from saleConfiguration to use as default values
  const { defaultCloudBluePlan, bundlePlan, defaultPlanPeriod, upsell } =
    !isEmpty(saleConfiguration)
      ? saleConfiguration.flatData
      : {
          defaultCloudBluePlan: undefined,
          bundlePlan: undefined,
          defaultPlanPeriod: undefined,
          upsell: undefined,
        }

  let planAlias = !isEmpty(defaultCloudBluePlan)
    ? defaultCloudBluePlan[0].flatData.alias
    : undefined

  let period = defaultPlanPeriod?.period

  const bundlePlanAlias = !isEmpty(bundlePlan)
    ? bundlePlan[0].flatData.alias
    : undefined

  const domainSearch = !isEmpty(saleConfiguration)
    ? getDomainsPoolId(saleConfiguration)
    : undefined

  // If ctaAxLink has configOptionValue defined, use it to override planAlias and period
  if (!isEmpty(ctaAxLink.configOptionValue) && !isEmpty(saleConfiguration)) {
    const configOption = saleConfiguration.flatData.optionsMapping?.find(
      (option) => option.value === ctaAxLink.configOptionValue
    )

    if (!isEmpty(configOption) && !isEmpty(configOption.cloudBluePlan)) {
      planAlias = configOption.cloudBluePlan[0].flatData.alias

      if (
        !isEmpty(configOption.period) &&
        !isEmpty(configOption.period.period)
      ) {
        period = configOption.period.period
      }
    }
  }

  // If ctaAxLink has period defined, use it to override period
  if (!isEmpty(ctaAxLink.period) && !isEmpty(ctaAxLink.period.period)) {
    period = ctaAxLink.period.period
  }

  // If a period was selected in the drawer UI, use it to override
  if (!isEmpty(selectedPeriod)) {
    period = selectedPeriod
  }

  const href = generateBonusUrl({
    p: planAlias,
    b: bundlePlanAlias,
    ax: getUpsellData(upsell),
    period,
    domainSearch,
    priceType: currentPriceType,
    isNotCheckedPeriod: ctaAxLink.isNotCheckedPeriod,
  })

  return href
}

export default {
  generateBonusUrl,
  getBasketUrl,
  getFinalizeUrl,
  getFailureUrl,
  getSuccessfulUrl,
  getDomainSearchUrl,
  getPasswordRecoverUrl,
  getPanelUrl,
  getBonusUrl,
  getDomainsBonusUrl,
  generateBonusUrlByCtaAxLink,
}
